/**
 * Utility functions for numeric operations, particularly for handling
 * percentfloor formatting and filtering with proper precision.
 */

/**
 * Floors a percentage value to the specified precision to match display formatting.
 * Uses multiplication instead of division to avoid floating point precision errors.
 *
 * This function is used to ensure that filtering behavior matches what users see
 * in the UI. For example, if a value of 0.6024 displays as "60.2%" (floored to
 * one decimal place), this function will return 0.602.
 *
 * @param value - The value to floor (typically in 0-1 range for percentages)
 * @param precision - The precision step (e.g., 0.1 for one decimal place, 0.01 for two)
 * @returns The floored value, or the original value if precision is invalid
 *
 * @example
 * floorToPercentPrecision(0.6024, 0.1) // returns 0.602 (displays as 60.2%)
 * floorToPercentPrecision(0.6028, 0.1) // returns 0.602 (displays as 60.2%)
 * floorToPercentPrecision(0.603, 0.1)  // returns 0.603 (displays as 60.3%)
 *
 * @remarks
 * The function avoids division by small decimals (like 0.1) which can cause
 * floating point errors. For example, (0.602 * 100) / 0.1 might evaluate to
 * 601.9999... due to floating point representation, which would floor to 601
 * instead of the expected 602. By using multiplication (0.602 * 100 * 10),
 * we avoid this precision issue.
 */
export function floorToPercentPrecision(value: number, precision: number): number {
    // Validate precision to avoid division by zero or invalid calculations
    if (precision <= 0 || !isFinite(precision)) {
        return value;
    }

    // Calculate the inverse to use multiplication instead of division
    const precisionInverse = Math.round(1 / precision);

    // Verify the inverse is valid
    if (!isFinite(precisionInverse) || precisionInverse <= 0) {
        return value;
    }

    // Floor to the specified precision using multiplication to avoid floating point errors
    const floored = Math.floor((value * 100) * precisionInverse) / precisionInverse / 100;

    // Round to a reasonable number of decimal places to avoid floating point trailing errors
    // Use precision to determine how many decimals we actually need
    const decimalPlaces = Math.max(0, -Math.floor(Math.log10(precision)) + 2);
    return Number(floored.toFixed(decimalPlaces));
}
